import { SendButton } from "@/chat/components/chat/ChatInput";
import { useHasPermission } from "@/features/clients/hooks/use-permissions";
import { useOperatorMessage, type Session } from "@/features/sessions";
import { useTranslation } from "@/features/utils/i18n";
import { Headset } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

const WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * A person of the team answering a customer on WhatsApp, Messenger or Instagram.
 *
 * What is written here is neither the agent's nor the customer's: it is stored as the
 * team's own message, shown under the author's name, and the agent stops answering what
 * was waiting for it. Meta only lets a business write freely for 24 hours after the
 * customer's last message, so past it the box is closed and says why, instead of
 * accepting a message that cannot leave.
 *
 * Mounted only on a conversation of those channels, for someone who may speak for the
 * company (`calls:reply`).
 */
export const OperatorComposer = ({ session }: { session: Session }) => {
  const { t } = useTranslation("calls");
  const canReply = useHasPermission("calls:reply");
  const send = useOperatorMessage();
  const [text, setText] = useState("");

  const channel = session.context?.meta_channel;
  if (!channel || !canReply) return null;

  const last = channel.last_customer_message_at ?? 0;
  const open = Date.now() - last < WINDOW_MS;

  const submit = async () => {
    const message = text.trim();
    if (!message || !session.id) return;
    try {
      await send.mutateAsync({ sessionId: session.id, message });
      setText("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("operator.error"),
      );
    }
  };

  return (
    <div className="border-t bg-background p-3 space-y-2">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Headset className="h-3.5 w-3.5" weight="fill" />
        {open
          ? t("operator.hint", { channel: t(`operator.channels.${channel.channel}`) })
          : t("operator.closed")}
      </p>
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
          disabled={!open || send.isPending}
          rows={2}
          placeholder={t("operator.placeholder")}
          className="min-h-[2.5rem] flex-1 resize-none rounded-md border bg-transparent px-3 py-2 text-sm disabled:opacity-50"
        />
        <SendButton
          onClick={() => void submit()}
          disabled={!open || !text.trim() || send.isPending}
        />
      </div>
    </div>
  );
};
